import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Mic, X } from 'lucide-react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Our own launcher for the Foyer "personal shopper" voice widget. The widget's
// built-in launcher is hidden via window.FoyerConfig.hideLauncher (index.html)
// below lg because it covered checkout CTAs; this round icon mirrors the chat icon on the right.
// On lg+ Foyer's own launcher shows, so we portal a dismiss button onto it instead.
// Dismissal lasts until the next page load.
const PersonalShopperButton = () => {
  const [available, setAvailable] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [foyerRoot, setFoyerRoot] = useState(null);
  const buttonRef = useRef(null);
  // Only manage focus for calls started from this launcher, so we never steal focus otherwise
  const openedHere = useRef(false);

  useEffect(() => {
    let observer;

    const sync = () => {
      const state = window.Foyer?.getState?.();
      setAvailable(!!state?.available);

      const root = document.getElementById('talklayer-root');
      if (root && !observer) {
        setFoyerRoot(root);
        observer = new MutationObserver(() =>
          setCallOpen(root.classList.contains('tl-call-open'))
        );
        observer.observe(root, { attributes: true, attributeFilter: ['class'] });
      }
    };

    window.Foyer?.ready?.then(sync);
    window.addEventListener('foyer:ready', sync);

    return () => {
      window.removeEventListener('foyer:ready', sync);
      observer?.disconnect();
    };
  }, []);

  // The launcher unmounts while a call is open, which would drop keyboard focus on <body>.
  // Move focus into the call UI on open, and back to the launcher on close.
  useEffect(() => {
    if (!openedHere.current) return;

    if (callOpen) {
      const frame = requestAnimationFrame(() => {
        const root = document.getElementById('talklayer-root');
        if (!root || root.contains(document.activeElement)) return;
        root.querySelector(FOCUSABLE)?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }

    openedHere.current = false;
    buttonRef.current?.focus();
  }, [callOpen]);

  const dismiss = () => {
    setDismissed(true);
    window.Foyer?.configure?.({ hideLauncher: true });
  };

  if (!available || callOpen || dismissed) return null;

  const dismissClass =
    'flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#1b1b1b] shadow-md ring-1 ring-black/10 transition-transform hover:scale-110';

  return (
    <>
      <div className="lg:hidden fixed bottom-4 left-4 z-[2147483000]">
        <span className="personal-shopper-greeting pointer-events-none absolute bottom-full left-0 mb-3 whitespace-nowrap rounded-[14px] bg-[#1b1b1b] px-3.5 py-2.5 text-[13px] font-medium text-[#f5f5f4] shadow-lg after:absolute after:left-[21px] after:top-full after:border-x-[7px] after:border-t-[7px] after:border-x-transparent after:border-t-[#1b1b1b] after:content-['']">
          Your personal shopper
        </span>
        <button
          type="button"
          aria-label="Talk to your personal shopper"
          ref={buttonRef}
          onClick={() => {
            openedHere.current = true;
            window.Foyer?.open();
          }}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#1b1b1b] text-white shadow-lg transition-transform hover:scale-105"
        >
          <Mic size={24} />
        </button>
        <button
          type="button"
          aria-label="Hide personal shopper"
          onClick={dismiss}
          className={`absolute -top-1.5 -right-1.5 ${dismissClass}`}
        >
          <X size={14} />
        </button>
      </div>

      {foyerRoot &&
        createPortal(
          <button
            type="button"
            aria-label="Hide personal shopper"
            onClick={dismiss}
            className={`hidden lg:flex absolute -top-2 -right-2 z-[1] ${dismissClass}`}
          >
            <X size={14} />
          </button>,
          foyerRoot
        )}
    </>
  );
};

export default PersonalShopperButton;
